import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { RealtimeChannel } from '@supabase/supabase-js';
import { useAuthContext } from '@/contexts/AuthContext';

interface NewItemsCounts {
  projects: number;
  tasks: number;
  taskNotes: number;
  files: number;
  team: number;
}

type SectionKey = 'projects' | 'tasks' | 'taskNotes' | 'files' | 'team';

export const useNewItemsIndicator = () => {
  const { user, initialized } = useAuthContext();
  const [counts, setCounts] = useState<NewItemsCounts>({
    projects: 0,
    tasks: 0,
    taskNotes: 0,
    files: 0,
    team: 0,
  });
  const [loading, setLoading] = useState(true);
  const [deferred, setDeferred] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const timestampsRef = useRef<Record<string, string>>({});

  // Defer initial fetch to not block first paint
  useEffect(() => {
    const timer = setTimeout(() => setDeferred(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  // Fetch stored timestamps from database
  const fetchStoredTimestamps = useCallback(async (uid: string): Promise<Record<string, string>> => {
    try {
      const { data, error } = await supabase
        .from('user_viewed_sections')
        .select('section_key, last_viewed_at')
        .eq('user_id', uid);

      if (error) {
        console.error('Error fetching viewed sections:', error);
        return {};
      }

      const timestamps: Record<string, string> = {};
      data?.forEach(row => {
        timestamps[row.section_key] = row.last_viewed_at;
      });
      return timestamps;
    } catch (error) {
      console.error('Error fetching viewed sections:', error);
      return {};
    }
  }, []);

  // Save timestamp to database - silently handles errors
  const saveTimestamp = useCallback(async (uid: string, sectionKey: string, timestamp: string) => {
    try {
      const { error } = await supabase
        .from('user_viewed_sections')
        .upsert(
          { 
            user_id: uid, 
            section_key: sectionKey, 
            last_viewed_at: timestamp 
          },
          { 
            onConflict: 'user_id,section_key',
            ignoreDuplicates: false 
          }
        );

      // Silently ignore all errors - this is non-critical UI state
      if (error && !error.message?.includes('AbortError') && !error.message?.includes('Load failed')) {
        console.warn('Viewed section save skipped:', sectionKey);
      }
    } catch {
      // Silently ignore - network aborts and load failures are expected
    }
  }, []);

  const fetchCounts = useCallback(async () => {
    if (!user) {
      setCounts({ projects: 0, tasks: 0, taskNotes: 0, files: 0, team: 0 });
      setLoading(false);
      return;
    }

    try {
      // Fetch timestamps from database
      const timestamps = await fetchStoredTimestamps(user.id);
      timestampsRef.current = timestamps;
      
      const defaultTimestamp = '1970-01-01T00:00:00Z';

      // Parallelize all count queries
      const [newProjects, newTasks, newTaskNotes, newFiles, newMembers] = await Promise.all([
        supabase.from('projects').select('id').gt('created_at', timestamps.projects || defaultTimestamp).neq('created_by', user.id),
        supabase.from('tasks').select('id').gt('created_at', timestamps.tasks || defaultTimestamp).neq('created_by', user.id),
        supabase.from('task_notes').select('id').gt('created_at', timestamps.taskNotes || defaultTimestamp).neq('created_by', user.id),
        supabase.from('files').select('id').gt('created_at', timestamps.files || defaultTimestamp).neq('created_by', user.id),
        supabase.from('profiles').select('id').gt('created_at', timestamps.team || defaultTimestamp).neq('id', user.id),
      ]);

      setCounts({
        projects: newProjects.data?.length || 0,
        tasks: newTasks.data?.length || 0,
        taskNotes: newTaskNotes.data?.length || 0,
        files: newFiles.data?.length || 0,
        team: newMembers.data?.length || 0,
      });
    } catch (error) {
      console.error('Error fetching new items counts:', error);
    } finally {
      setLoading(false);
    }
  }, [user, fetchStoredTimestamps]);

  const markAsViewed = useCallback(async (section: SectionKey) => {
    if (!user) return;
    
    const now = new Date().toISOString();
    
    // Update local state immediately for responsiveness
    setCounts(prev => ({ ...prev, [section]: 0 }));
    timestampsRef.current[section] = now;
    
    // Save to database
    await saveTimestamp(user.id, section, now);
  }, [user, saveTimestamp]);

  // Mark both tasks and taskNotes as viewed
  const markTasksAsViewed = useCallback(async () => {
    if (!user) return;
    
    const now = new Date().toISOString();
    
    // Update local state immediately for responsiveness
    setCounts(prev => ({ ...prev, tasks: 0, taskNotes: 0 }));
    timestampsRef.current.tasks = now;
    timestampsRef.current.taskNotes = now;
    
    // Save both to database
    await Promise.all([
      saveTimestamp(user.id, 'tasks', now),
      saveTimestamp(user.id, 'taskNotes', now),
    ]);
  }, [user, saveTimestamp]);

  useEffect(() => {
    if (!deferred || !user || !initialized) return;
    
    fetchCounts();

    // Subscribe to realtime updates
    const channel = supabase
      .channel('new-items-indicator')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'projects' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'tasks' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'task_notes' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'files' },
        () => fetchCounts()
      )
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'profiles' },
        () => fetchCounts()
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
    };
  }, [deferred, user, initialized, fetchCounts]);

  return {
    counts,
    loading,
    markAsViewed,
    markTasksAsViewed,
    refetch: fetchCounts,
  };
};
